import React, { useState } from 'react';
import { submitForm } from '../utils/api';
import './FormPage.css';

// Step 1: Personal Information
const PersonalInfoStep = ({ formData, onChange, onNext }) => {
  const [errors, setErrors] = useState({});
  
  const validate = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleNext = (e) => {
    e.preventDefault();
    if (validate()) {
      onNext();
    }
  };
  
  return (
    <div className="form-step">
      <h3>Step 1: Personal Information</h3>
      <form onSubmit={handleNext}>
        <div className="form-field">
          <label>First Name</label>
          <input
            type="text"
            value={formData.firstName || ''}
            onChange={(e) => onChange('firstName', e.target.value)}
          />
          {errors.firstName && <div className="form-error">{errors.firstName}</div>}
        </div>
        
        <div className="form-field">
          <label>Last Name</label>
          <input
            type="text"
            value={formData.lastName || ''}
            onChange={(e) => onChange('lastName', e.target.value)}
          />
          {errors.lastName && <div className="form-error">{errors.lastName}</div>}
        </div>
        
        <div className="form-field">
          <label>Email</label>
          <input
            type="email"
            value={formData.email || ''}
            onChange={(e) => onChange('email', e.target.value)}
          />
          {errors.email && <div className="form-error">{errors.email}</div>}
        </div>
        
        <div className="form-field">
          <label>Phone (optional)</label>
          <input
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => onChange('phone', e.target.value)}
          />
        </div>
        
        <button type="submit" className="next-button">Next</button>
      </form>
    </div>
  );
};

// Step 2: Address Information
const AddressStep = ({ formData, onChange, onNext, onBack }) => {
  const [errors, setErrors] = useState({});
  
  const validate = () => {
    const newErrors = {};
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.zipCode) newErrors.zipCode = 'Zip code is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleNext = (e) => {
    e.preventDefault();
    if (validate()) {
      onNext();
    }
  };
  
  return (
    <div className="form-step">
      <h3>Step 2: Address Information</h3>
      <form onSubmit={handleNext}>
        <div className="form-field">
          <label>Address</label>
          <input
            type="text"
            value={formData.address || ''}
            onChange={(e) => onChange('address', e.target.value)}
          />
          {errors.address && <div className="form-error">{errors.address}</div>}
        </div>
        
        <div className="form-field">
          <label>City</label>
          <input
            type="text"
            value={formData.city || ''}
            onChange={(e) => onChange('city', e.target.value)}
          />
          {errors.city && <div className="form-error">{errors.city}</div>}
        </div>
        
        <div className="form-field">
          <label>State/Province</label>
          <input
            type="text"
            value={formData.state || ''}
            onChange={(e) => onChange('state', e.target.value)}
          />
        </div>
        
        <div className="form-field">
          <label>Zip/Postal Code</label>
          <input
            type="text"
            value={formData.zipCode || ''}
            onChange={(e) => onChange('zipCode', e.target.value)}
          />
          {errors.zipCode && <div className="form-error">{errors.zipCode}</div>}
        </div>
        
        <div className="form-field">
          <label>Country</label>
          <select
            value={formData.country || 'United States'}
            onChange={(e) => onChange('country', e.target.value)}
          >
            <option value="United States">United States</option>
            <option value="Canada">Canada</option>
            <option value="United Kingdom">United Kingdom</option>
            <option value="Australia">Australia</option>
            <option value="Germany">Germany</option>
            <option value="France">France</option>
            <option value="Other">Other</option>
          </select>
        </div>
        
        <div className="form-buttons">
          <button type="button" onClick={onBack} className="back-button">Back</button>
          <button type="submit" className="next-button">Next</button>
        </div>
      </form>
    </div>
  );
};

// Step 3: Review and Submit
const ReviewStep = ({ formData, onSubmit, onBack }) => {
  return (
    <div className="form-step">
      <h3>Step 3: Review Information</h3>
      
      <div className="review-section">
        <h4>Personal Information</h4>
        <div className="review-item">
          <span className="review-label">Name:</span>
          <span className="review-value">{formData.firstName} {formData.lastName}</span>
        </div>
        <div className="review-item">
          <span className="review-label">Email:</span>
          <span className="review-value">{formData.email}</span>
        </div>
        {formData.phone && (
          <div className="review-item">
            <span className="review-label">Phone:</span>
            <span className="review-value">{formData.phone}</span>
          </div>
        )}
      </div>
      
      <div className="review-section">
        <h4>Address Information</h4>
        <div className="review-item">
          <span className="review-label">Address:</span>
          <span className="review-value">{formData.address}</span>
        </div>
        <div className="review-item">
          <span className="review-label">City:</span>
          <span className="review-value">{formData.city}</span>
        </div>
        {formData.state && (
          <div className="review-item">
            <span className="review-label">State/Province:</span>
            <span className="review-value">{formData.state}</span>
          </div>
        )}
        <div className="review-item">
          <span className="review-label">Zip/Postal Code:</span>
          <span className="review-value">{formData.zipCode}</span>
        </div>
        <div className="review-item">
          <span className="review-label">Country:</span>
          <span className="review-value">{formData.country}</span>
        </div>
      </div>
      
      <div className="form-buttons">
        <button type="button" onClick={onBack} className="back-button">Back</button>
        <button type="button" onClick={onSubmit} className="submit-button">Submit</button>
      </div>
    </div>
  );
};

// Multi-step form main component
const MultiStepForm = ({ onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    country: 'United States'
  });
  
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };
  
  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };
  
  const handleSubmit = () => {
    onSubmit(formData);
  };
  
  return (
    <div className="multi-step-form">
      <div className="form-progress">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>1</div>
        <div className="progress-line"></div>
        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>2</div>
        <div className="progress-line"></div>
        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
      </div>
      
      {currentStep === 1 && (
        <PersonalInfoStep
          formData={formData}
          onChange={handleChange}
          onNext={nextStep}
        />
      )}
      
      {currentStep === 2 && (
        <AddressStep
          formData={formData}
          onChange={handleChange}
          onNext={nextStep}
          onBack={prevStep}
        />
      )}
      
      {currentStep === 3 && (
        <ReviewStep
          formData={formData}
          onSubmit={handleSubmit}
          onBack={prevStep}
        />
      )}
    </div>
  );
};

const MultiStepFormPage = () => {
  const [apiEnvironment, setApiEnvironment] = useState('simulation');
  const [submissionResult, setSubmissionResult] = useState(null);
  
  const handleSubmit = async (formValues) => {
    console.log('Multi-step form values:', formValues);
    
    try {
      const result = await submitForm('registration', formValues, null, apiEnvironment);
      console.log('Submission result:', result);
      setSubmissionResult(result);
      return result;
    } catch (error) {
      console.error('Submission error:', error);
      setSubmissionResult({
        success: false,
        message: error.message || 'An unknown error occurred'
      });
      throw error;
    }
  };
  
  return (
    <div>
      <h1 className="page-title">Multi-Step Form Example</h1>
      
      <div className="page-description">
        <p>This example demonstrates a multi-step form implementation with validation between steps. Multi-step forms improve user experience by breaking complex forms into manageable sections.</p>
      </div>
      
      <div className="form-options">
        <div className="option-group">
          <label htmlFor="api-environment">API Environment:</label>
          <select
            id="api-environment"
            value={apiEnvironment}
            onChange={(e) => setApiEnvironment(e.target.value)}
          >
            <option value="simulation">Simulation (No API)</option>
            <option value="local">Local API (http://localhost:8787)</option>
            <option value="production">Production API</option>
          </select>
        </div>
      </div>
      
      {submissionResult && (
        <div className={`submission-result ${submissionResult.success ? 'success' : 'error'}`}>
          <h3>{submissionResult.success ? 'Form Submitted Successfully' : 'Submission Failed'}</h3>
          <p>{submissionResult.message}</p>
          {submissionResult.id && <p>Reference ID: {submissionResult.id}</p>}
        </div>
      )}
      
      <div className="form-container-wrapper">
        <MultiStepForm onSubmit={handleSubmit} />
      </div>
    </div>
  );
};

export default MultiStepFormPage; 